import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { listIntegrations } from "@/api/client";
import type { IntegrationsView } from "@/api/types";

/**
 * One calendar-connection state for a whole page, instead of one per panel.
 *
 * <p>The profile page renders three things that each need to know whether a calendar is connected:
 * the setup checklist, the connections panel, and the "when a meeting is auto-booked" choice. Each
 * used to call {@code listIntegrations()} for itself and keep the answer in its own state, which
 * meant disconnecting a calendar updated the panel it was disconnected from and nothing else. The
 * delivery choice went on offering "Add to my calendar" as a live option, and went on saying "Your
 * calendar's connected", until the page was reloaded.</p>
 *
 * <p>Anything that changes a connection calls {@link IntegrationsState#refresh}, and every consumer
 * on the page moves together.</p>
 */
export interface IntegrationsState {
  /** The connections, or null if the fetch has not resolved (or failed). */
  iv: IntegrationsView | null;
  /**
   * False until the first fetch settles. Distinct from {@code iv === null}, which a failed fetch
   * also produces: a consumer that must not assert anything while loading needs to tell those
   * apart, and "no calendars" is a real answer while "not yet known" is not.
   */
  loaded: boolean;
  /** Re-read the connections. Call after anything that connects, disconnects or repairs one. */
  refresh: () => void;
  /**
   * The provider a consent screen just returned from, or null.
   *
   * <p>Read from `?connected=` and consumed exactly once, here rather than in whichever panel
   * happens to mount first: two components strip the same param and the loser never sees it. The
   * connections panel needs it for its banner and the delivery switch needs it to animate, so it
   * cannot belong to either of them.</p>
   *
   * <p>Set on a repair as well as a first connection, and that is correct rather than a nag: while
   * a connection sits in ERROR, delivery falls back to email, so repairing one genuinely does move
   * meetings back onto the calendar and is worth saying out loud.</p>
   */
  justConnected: string | null;
  /** The `?error=` code from a failed consent round trip, consumed the same way. */
  connectError: string | null;
  /** Drop both, once they have been shown. */
  dismissCallbackResult: () => void;
}

const NOT_PROVIDED: IntegrationsState = {
  iv: null,
  loaded: false,
  refresh: () => {},
  justConnected: null,
  connectError: null,
  dismissCallbackResult: () => {},
};

const IntegrationsContext = React.createContext<IntegrationsState | null>(null);

export function IntegrationsProvider({ children }: { children: React.ReactNode }) {
  const [iv, setIv] = React.useState<IntegrationsView | null>(null);
  const [loaded, setLoaded] = React.useState(false);

  const refresh = React.useCallback(() => {
    // Deliberately does not clear `iv` first. The delivery choice reads this to decide whether
    // "Add to my calendar" is offered at all, so blanking it mid-refresh would flash that option
    // back to selectable for as long as the request takes — the exact state this file exists to
    // prevent. The old answer is wrong for a moment; no answer is wrong in a way that is clickable.
    listIntegrations()
      .then((next) => {
        setIv(next);
        setLoaded(true);
      })
      .catch(() => {
        // Leave the last known state in place and let consumers keep rendering it. A failed
        // refresh is not evidence that anything changed.
        setLoaded(true);
      });
  }, []);

  React.useEffect(refresh, [refresh]);

  // Consume the consent screen's redirect params exactly once, then strip them so a page refresh
  // does not replay the banner or re-run the switch animation.
  const [searchParams, setSearchParams] = useSearchParams();
  const [callback, setCallback] = React.useState<{ connected: string | null; error: string | null }>(
    { connected: null, error: null },
  );
  React.useEffect(() => {
    const connected = searchParams.get("connected");
    const error = searchParams.get("error");
    if (!connected && !error) return;
    setCallback({ connected, error });
    const next = new URLSearchParams(searchParams);
    next.delete("connected");
    next.delete("error");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const dismissCallbackResult = React.useCallback(
    () => setCallback({ connected: null, error: null }),
    [],
  );

  const value = React.useMemo<IntegrationsState>(
    () => ({
      iv,
      loaded,
      refresh,
      justConnected: callback.connected,
      connectError: callback.error,
      dismissCallbackResult,
    }),
    [iv, loaded, refresh, callback, dismissCallbackResult],
  );
  return <IntegrationsContext.Provider value={value}>{children}</IntegrationsContext.Provider>;
}

/**
 * The page's shared connection state.
 *
 * <p>Returns an empty, never-loading state outside a provider rather than throwing, because
 * {@link ProfileForm} is also rendered read-only by the admin user page, where the integrations of
 * the signed-in admin are not the ones being viewed and must not be fetched at all.</p>
 */
export function useIntegrations(): IntegrationsState {
  return React.useContext(IntegrationsContext) ?? NOT_PROVIDED;
}
